import Score, {ScoreData} from "../../Shared/Components/Score";
import {Task} from "../../Shared/Model/SafetyData";
import SafetyPuzzle from "./SafetyPuzzle";
import {useEffect, useState} from "react";
import SafetyManager, {SafetyPlayState} from "../SafetyManager";
import SafetyPhoto from "./SafetyPhoto";
import {ManagerProvider} from "../SafetyManagerContext";
import Footer from "../../Shared/Components/Footer";
import {NextButton} from "../../Shared/Components/NextButton";
import {SkipButton} from "../../Shared/Components/SkipButton";
import {GameHint} from "../../Quiz/Components/GameHint";
import SafetyMatching from "./SafetyMatching";
import useSound from "use-sound";
import SafetyPageTitle from "./SafetyPageTitle";
import SafetyMove from "./SafetyMove";
import {Pair} from "../../Shared/Model/GameData";
import {scrollToTop} from "../../Shared/Util/Functions";

export interface SafetyGameProps {
    title: string,
    subtitle: string
    tasks: [Task]
    onFinish: (score: ScoreData) => void
    onCancel: () => void
}

export interface SafetyTaskProps {
    gameProps: SafetyGameProps
    task: Task
    isAnsweredCorrect?: boolean
    selectedAnswerIndex?: number
    solve: boolean
    onLoaded: () => void
    onFinish: (correct: boolean, selectedAnswerIndex?: number) => void
}

export interface SafetyMatchingTaskProps extends SafetyTaskProps {
    pairs: Pair[]
    completed: number[]
    leftClicked: number | undefined
    rightClicked: number | undefined
    leftWrongClicked: number | undefined
    rightWrongClicked: number | undefined
    onLeftClicked: (pair, index) => boolean
    onRightClicked: (pair, index) => boolean
    onClickResult: (pair, index, correct) => void
}

export interface SafetyMoveTaskProps extends SafetyTaskProps {
    droppedCorrectly: number[]
    droppedIncorrectly: number[]
    onItemDragged: (id, correct, toCorrectStage) => boolean
    onItemDropped: (id, correct) => void
}

export default function SafetyPlay(props: SafetyGameProps) {

    const [manager,] = useState<SafetyManager>(() => new SafetyManager(props.tasks))
    const [state, setState] = useState<SafetyPlayState>(manager.state())
    const [solve, setSolve] = useState(false)

    const [soundGood] = useSound("sounds/good.mp3");
    const [soundWrong] = useSound("sounds/wrong.mp3");

    const next = () => {
        if (!solve && state.isAnswered && !state.isAnswerCorrect) {
            setSolve(true)
        } else if (manager.next()) {
            setSolve(false)
            setState(manager.state())
        } else {
            props.onFinish(manager.score())
        }
    }

    const skip = () => {
        if (!state.isAnswered) {
            setSolve(true)
        }
        soundWrong()
        manager.skip()
        setState(manager.state())
    }

    const answerAndSolve = (correct: boolean, selectedAnswerIndex?: number) => {
        answer(correct, selectedAnswerIndex, true)
    }

    const answer = (correct: boolean, selectedAnswerIndex?: number, solve: boolean = false) => {
        if (manager.canAnswer()) {
            manager.answer(correct, selectedAnswerIndex)
            setSolve(solve)
            setState(manager.state())
        }
    }

    useEffect(() => {
        if (state.isAnswered) {
            if (state.isAnswerCorrect) {
                soundGood()
            } else {
                soundWrong()
            }
            scrollToTop()
        }
    }, [state])


    let task = manager.currentTask()
    let view

    if (task === undefined) {
        return <>Brak zadań</>
    }

    switch (task.type) {
        case "puzzle": view = <SafetyPuzzle
            gameProps={props}
            task={manager.currentTask()}
            solve={solve}
            onLoaded={scrollToTop}
            onFinish={() => answer(true)}/>; break;
        case "photo": view = <SafetyPhoto
            gameProps={props}
            task={manager.currentTask()}
            solve={solve}
            isAnsweredCorrect={state.isAnswerCorrect}
            selectedAnswerIndex={state.selectedAnswerIndex}
            onLoaded={scrollToTop}
            onFinish={answerAndSolve}/>; break;
        case "move": view = <SafetyMove
            gameProps={props}
            solve={solve}
            task={manager.currentTask()}
            droppedCorrectly={manager.moveData().correct}
            droppedIncorrectly={manager.moveData().wrong}
            onLoaded={scrollToTop}
            onItemDragged={(id, correct, toCorrectStage) => { const result = manager.handleMoveItem(id, correct, toCorrectStage); setState(manager.state()); return result; }}
            onItemDropped={(id, correct) => { if(correct) soundGood(); } }
            onFinish={answerAndSolve}/>; break;
        case "matching": view = <SafetyMatching
            gameProps={props}
            task={manager.currentTask()}
            pairs={manager.filteredPairs()}
            leftClicked={state.pairs.leftPairClicked}
            rightClicked={state.pairs.rightPairClicked}
            leftWrongClicked={state.pairs.leftWrongPairClicked}
            rightWrongClicked={state.pairs.rightWrongPairClicked}
            completed={state.pairs.pairsCompleted}
            solve={solve}
            onLoaded={scrollToTop}
            onFinish={answer}
            onLeftClicked={(pair, index) => { const result = manager.handleLeftPairClicked(pair, index); if(!state.isAnswered) setState(manager.state()); return result; }}
            onRightClicked={(pair, index) => { const result = manager.handleRightPairClicked(pair, index); if(!state.isAnswered) setState(manager.state()); return result; }}
            onClickResult={(pair, index, correct) => { if (correct) soundGood() } }
        />; break;
        default: view = <></>; break;
    }

    return <ManagerProvider value={manager}>
        <SafetyPageTitle title={props.title} className={task.biome === "MOUNTAINS" ? "white" : ""} />
        <div className="biome-background">
            { task.biome === "MOUNTAINS" && <img src="images/safety/mountains-logo.png" className="biome-logo" alt=""/> }
            <div className={"biome-water " + (task.biome === "WATER" && "active")} style={{backgroundImage: "url('images/safety/biome-water.jpg')"}}></div>
            <div className={"biome-mountains " + (task.biome === "MOUNTAINS" && "active")} style={{backgroundImage: "url('images/safety/biome-mountains.jpg')"}}></div>
        </div>

        <div className="safety-play">
            <GameHint className="top" text={manager.hint()} isCorrect={state.isAnswerCorrect} />
            <div className={"task " + task.type}>
                <h2>{task.question}</h2>
                {view}
            </div>
            {manager.shouldDisplaySkip() && <SkipButton onClick={skip} showTitle={true}/> }
            {manager.shouldDisplayNext() && <NextButton onClick={next} showTitle={true}/>}
            <Score data={manager.score()} backgroundImage="images/safety/score.png"
                   helperImage={"images/safety/" + (task.biome === "WATER" ? "helper.png" : "helper-mountain.png")}/>
        </div>

        <Footer home={props.onCancel}/>
    </ManagerProvider>
}
